import {
    AdminApi,
    ChargemasterApi,
    ClaimApi,
    ClaimTransmissionStatusType,
    ClaimType,
    ClaimWorkerResponse,
    ClearinghousePayerApi,
    CodingApi,
    CollectionApi,
    CoreFetch,
    createGroupRequestFilters,
    createRequestFilters,
    DocumentApi,
    EligibilityApi,
    EmailApi,
    EstimateApi,
    FacilityApi,
    FaxApi,
    FilterMethod,
    GuarantorApi,
    HL7Api,
    ICustomFilter,
    InstitutionalApi,
    Invoice,
    InvoiceApi,
    InvoiceType,
    LetterApi,
    ListSupportClaimsFilterClaimTypeEnum,
    ListSupportClaimsOrderEnum,
    ListSupportClaimsRequest,
    ListSupportHL7MessagesOrderEnum,
    MailApi,
    MessageApi,
    Nynf3Api,
    ObjectIdResponse,
    PaymentApi,
    PostClaimGuarantorResponsibilityRequest,
    PostSupportCodingRequest,
    ProfessionalApi,
    ProviderApi,
    ProviderCreateClaimInstitutionalPayload,
    ProviderCreateClaimProfessionalPayload,
    SellerApi,
    StatusApi,
    SupportAccount,
    SupportAdjustCollectionStrategyPayload,
    SupportChargemasterItem,
    SupportClaim,
    SupportClaimEligibility,
    ClaimGuarantorResponsibility,
    SupportClaimPayment,
    SupportClaimSortType,
    SupportClaimTransmission,
    SupportClearinghousePayer,
    SupportCoding,
    SupportWorkerFunction,
    SupportComment,
    SupportCollectionInvoiceEvent,
    SupportCollectionPayment,
    SupportCreateClaimInstitutionalPayload,
    SupportCreateClaimProfessionalPayload,
    SupportCreateCodingPayload,
    SupportCreateInstitutionalClaimPayload,
    SupportCreateProfessionalClaimPayload,
    SupportCreateStatusPayload,
    SupportDocument,
    SupportEstimate,
    SupportFacility,
    SupportGuarantor,
    SupportHL7Message,
    SupportHL7SortType,
    SupportInstitutionalClaim,
    SupportInstitutionalClaimTemplate,
    SupportLetter,
    SupportMail,
    SupportMessage,
    SupportNynf3Claim,
    SupportNynf3ClaimTemplate,
    SupportProfessionalClaim,
    SupportProfessionalClaimTemplate,
    SupportProvider,
    SupportReceiveFax,
    SupportSeller,
    SupportSendFax,
    SupportSettleClaimPayload,
    SupportStatus,
    SupportTag,
    SupportUpdateClaimPayload,
    SupportUpdateInstitutionalClaimPayload,
    SupportUpdateProfessionalClaimPayload,
    TagApi,
    Transaction,
    TransmissionApi,
    SupportClaimDetails,
    PayerResponsibilityType,
    TransmissionType,
    SupportSendEmail,
    SupportMedicalClaimResponse,
    SupportProfessionalTemplateServiceLine,
    ListSearchProviderClaimsRequest,
    ProviderClaim,
    ListSearchSupportClaimsRequest,
    SupportPhone,
    PhoneApi,
} from '@enter/core';
import _ from 'lodash';
import { action, computed, observable, runInAction } from 'mobx';
import * as React from 'react';
import { IFetchResourceRequest } from '../services/store/store.service';
import { InstitutionalTemplatesStore } from './institutional-templates.store';
import { ProfessionalTemplatesStore } from './professional-templates.store';
import { IResourceStore, ResourceStore } from './resource.store';

type TagEventCreate = {
    eventType: 'create';
    accountId: string | undefined;
};

type TagEventDisable = {
    eventType: 'disable';
    accountId: string | undefined;
};

export type TagEvent = (TagEventDisable | TagEventCreate) & {
    id?: string;
    name: string;
    created: string;
};

export interface SupportCreateClaimPayload {
    claimType: ClaimType;
    supportCreateClaimProfessionalPayload?: SupportCreateClaimProfessionalPayload;
    supportCreateClaimInstitutionalPayload?: SupportCreateClaimInstitutionalPayload;
}

export interface IRecord<T> {
    data: T;
    loaded: boolean;
    // Change `loaded` to `status` if error status is required
}

function createRecord<T>(data: T, loaded = false): IRecord<T> {
    return {
        data,
        loaded,
    };
}

export class ClaimsStore extends ResourceStore<SupportClaim> implements IResourceStore<SupportClaim> {
    public static getDisplayName(claim: SupportClaim) {
        const baseName = _.compact([claim.patientFirst, claim.patientLast]).join(' ');
        const patientName = baseName ? _.compact([baseName, claim.patientSuffix]).join(' ') : '';
        const displayName = _.compact([patientName || claim.primaryPayerName, claim.type, claim.fromDos]).join(', ');

        return displayName;
    }

    public static getDisplayNameShort(claim: SupportClaim) {
        const baseName = _.compact([claim.patientFirst, claim.patientLast]).join(' ');
        const patientName = baseName ? _.compact([baseName, claim.patientSuffix]).join(' ') : '';
        const displayName = _.compact([patientName || claim.primaryPayerName]).join(', ');

        return displayName;
    }

    public static getInfo(claim: SupportClaim): React.ReactElement {
        const { chargeAmount } = claim;

        return (
            <div>
                {chargeAmount && (
                    <div>
                        {chargeAmount.amount} {chargeAmount.currency}
                    </div>
                )}
                <small>{claim.id}</small>
            </div>
        );
    }

    public static getId(claim: SupportClaim): string {
        return claim.id || '';
    }

    public static async combinedSearchForSupportClaimsByTerm(query: string): Promise<SupportClaim[]> {
        const listClaims = CoreFetch<ClaimApi>(ClaimApi).listSearchSupportClaims({ search: [query] });
        const claims = await Promise.all([listClaims]);
        const flattenClaims = _.uniqBy(_.flatten(claims), 'id');
        return _.map(flattenClaims, claim => this.updateClaimStatus(claim));
    }

    public static async getByQuery(query: string): Promise<SupportClaim[]> {
        // TODO: replace by 'OR' filterin when it is implemented.
        // Use two calls for now
        const getByName = (): Promise<SupportClaim[]> => {
            const filters: ICustomFilter[] = [
                {
                    filterId: 'filterFirstLast',
                    filterMethod: FilterMethod.LK,
                    filterType: 'String',
                    value: query,
                },
            ];

            const reqFilters = createRequestFilters(filters);
            return CoreFetch<ClaimApi>(ClaimApi).listSupportClaims({
                ...reqFilters,
            });
        };

        const claims = await Promise.all([getByName(), ClaimsStore.getById(query)]);
        const flattenClaims = _.uniqBy(_.flatten(claims), 'id');
        return _.map(flattenClaims, claim => this.updateClaimStatus(claim));
    }

    public static async getById(id: string): Promise<SupportClaim[]> {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            },
        ];

        const reqFilters = createRequestFilters(filters);
        const claims = await CoreFetch<ClaimApi>(ClaimApi).listSupportClaims({
            ...reqFilters,
        });

        return _.map(claims, claim => this.updateClaimStatus(claim));
    }

    public static async getByIds(ids: string[], omitActive = false, claimType?: ClaimType): Promise<SupportClaim[]> {
        if (_.isEmpty(ids)) {
            return [];
        }

        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters, omitActive);

        const listSupportClaimsRequest: ListSupportClaimsRequest = {
            ...reqFilters,
            filterClaimType: claimType
                ? claimType === ClaimType.PROFESSIONAL
                    ? ListSupportClaimsFilterClaimTypeEnum.PROFESSIONAL
                    : ListSupportClaimsFilterClaimTypeEnum.INSTITUTIONAL
                : undefined,
        };

        const claims = await CoreFetch<ClaimApi>(ClaimApi).listSupportClaims(listSupportClaimsRequest);
        return _.map(claims, claim => this.updateClaimStatus(claim));
    }

    public static async fetchBundle(claimBundleId: string): Promise<SupportClaim[]> {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterClaimBundleId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: claimBundleId,
            },
        ];

        const reqFilters = createRequestFilters(filters);
        const claims = await CoreFetch<ClaimApi>(ClaimApi).listSupportClaims({
            ...reqFilters,
        });

        return _.map(claims, claim => this.updateClaimStatus(claim));
    }

    public static async getOne(id: string): Promise<SupportClaim> {
        const claim = await CoreFetch<ClaimApi>(ClaimApi).getSupportClaim({
            id,
        });

        return this.updateClaimStatus(claim);
    }

    public static async createProfessionalClaim(
        payload: SupportCreateClaimProfessionalPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).createSupportClaimProfessional({
            supportCreateClaimProfessionalPayload: payload,
        });
    }

    public static async createProfessionalProviderClaim(
        payload: ProviderCreateClaimProfessionalPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).createProviderClaimProfessional({
            providerCreateClaimProfessionalPayload: payload,
        });
    }

    public static async createInstitutionalClaim(
        payload: SupportCreateClaimInstitutionalPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).createSupportClaimInstitutional({
            supportCreateClaimInstitutionalPayload: payload,
        });
    }

    public static async createInstitutionalProviderClaim(
        payload: ProviderCreateClaimInstitutionalPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).createProviderClaimInstitutional({
            providerCreateClaimInstitutionalPayload: payload,
        });
    }

    public static async createCoding(
        supportCreateCodingPayload: SupportCreateCodingPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<CodingApi>(CodingApi).postSupportCoding({
            supportCreateCodingPayload,
        });
    }

    public static async update(id: string, payload: SupportUpdateClaimPayload): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).updateSupportClaim({
            id,
            supportUpdateClaimPayload: payload,
        });
    }

    public static async updateMaximumGuarantorResponsibility(id: string, payload: ClaimGuarantorResponsibility) {
        return CoreFetch<ClaimApi>(ClaimApi).postClaimGuarantorResponsibility({
            id,
            claimGuarantorResponsibility: payload,
        });
    }

    public static async updateProfessional(
        id: string,
        payload: SupportUpdateProfessionalClaimPayload,
    ): Promise<ObjectIdResponse> {
        throw new Error('API no longer support this action');
    }

    public static async updateInstitutional(
        id: string,
        payload: SupportUpdateInstitutionalClaimPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<InstitutionalApi>(InstitutionalApi).updateSupportInstitutionalClaim({
            id,
            supportUpdateInstitutionalClaimPayload: payload,
        });
    }

    public static async createProfessional(payload: SupportCreateProfessionalClaimPayload): Promise<ObjectIdResponse> {
        return CoreFetch<ProfessionalApi>(ProfessionalApi).createSupportProfessionalClaim({
            supportCreateProfessionalClaimPayload: payload,
        });
    }

    public static async createInstitutional(
        payload: SupportCreateInstitutionalClaimPayload,
    ): Promise<ObjectIdResponse> {
        return CoreFetch<InstitutionalApi>(InstitutionalApi).createSupportInstitutionalClaim({
            supportCreateInstitutionalClaimPayload: payload,
        });
    }

    public static async enablePatientCollections(id: string): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).postSupportClaimEnablePatientCollections({
            id,
        });
    }

    public static async disablePatientCollections(id: string): Promise<ObjectIdResponse> {
        return CoreFetch<ClaimApi>(ClaimApi).postSupportClaimDisablePatientCollections({
            id,
        });
    }

    public static async getMedicalClaims(
        claimId: string,
        claimType: ClaimType,
    ): Promise<(SupportProfessionalClaim | SupportInstitutionalClaim)[]> {
        switch (claimType) {
            case ClaimType.PROFESSIONAL:
                return CoreFetch<ProfessionalApi>(ProfessionalApi).listSupportProfessionalClaims({
                    filterClaimId: [`EQ:${claimId}`],
                    filterActive: `EQ:1`,
                });
            case ClaimType.INSTITUTIONAL:
                return CoreFetch<InstitutionalApi>(InstitutionalApi).listSupportInstitutionalClaims({
                    filterClaimId: [`EQ:${claimId}`],
                    filterActive: `EQ:1`,
                });
            default:
                throw new Error('Not implemented');
        }
    }

    public static async getHl7(claimId: string): Promise<SupportHL7Message[]> {
        return CoreFetch<HL7Api>(HL7Api).listSupportHL7Messages({
            filterClaimId: [`EQ:${claimId}`],
        });
    }

    @computed
    public get medicalClaims(): IRecord<(SupportProfessionalClaim | SupportInstitutionalClaim)[]> {
        const data = [...this.professionals.data, ...this.institutionals.data];
        const loaded = this.professionals.loaded || this.institutionals.loaded;

        return createRecord(data, loaded);
    }

    public static updateClaimStatus(claim: SupportClaim): SupportClaim {
        // NB: update status to ADJUDICATED if payments exist.
        if (claim.transmissionStatus === ClaimTransmissionStatusType.REJECTED) {
            return claim;
        }

        if ((claim.payments || 0) > 0) {
            claim.transmissionStatus = ClaimTransmissionStatusType.ADJUDICATED;
        }
        return claim;
    }

    @observable public professionals: IRecord<SupportProfessionalClaim[]> = createRecord([]);
    @observable public institutionals: IRecord<SupportInstitutionalClaim[]> = createRecord([]);
    @observable public nynf3: SupportNynf3Claim[] = [];
    @observable public eligibilities: IRecord<SupportClaimEligibility[]> = createRecord([]);
    @observable public estimates: SupportEstimate[] = [];
    @observable public transmissions: SupportClaimTransmission[] = [];
    @observable public documents: SupportDocument[] = [];
    @observable public phones: SupportPhone[] = [];
    @observable public payments: IRecord<SupportClaimPayment[]> = createRecord([]);
    // Inactive payments for the claim (loaded on demand for timeline)
    @observable public inactivePayments: IRecord<SupportClaimPayment[]> = createRecord([]);
    @observable public collectionPayments: IRecord<SupportCollectionPayment[]> = createRecord([]);
    @observable public chargemasterItems: IRecord<SupportChargemasterItem[]> = createRecord([], true);
    @observable public professionalTemplate: IRecord<SupportProfessionalClaimTemplate | undefined> = createRecord(
        undefined,
        true,
    );
    @observable public institutionalTemplate: IRecord<SupportInstitutionalClaimTemplate | undefined> = createRecord(
        undefined,
        true,
    );
    @observable public nynf3Template: SupportNynf3ClaimTemplate | undefined = undefined;
    @observable public invoices: IRecord<Invoice[]> = createRecord([], true);
    @observable public letters: SupportLetter[] = [];
    @observable public codings: SupportCoding[] = [];
    @observable public workerFunctions: SupportWorkerFunction[] = [];
    @observable public comments: SupportComment[] = [];

    @observable public collectionInvoiceEvents: SupportCollectionInvoiceEvent[] = [];
    @observable public mails: SupportMail[] = [];
    @observable public faxReceive: SupportReceiveFax[] = [];
    @observable public emailSend: SupportSendEmail[] = [];
    @observable public faxSend: SupportSendFax[] = [];
    @observable public transactions: Transaction[] = [];
    @observable public hl7: SupportHL7Message[] = [];
    @observable public messages: SupportMessage[] = [];
    @observable public tags: TagEvent[] = [];
    @observable public statuses: SupportStatus[] = [];
    @observable public providers: SupportProvider[] = [];
    @observable public facility: SupportFacility | undefined = undefined;
    @observable public seller: SupportSeller | undefined = undefined;
    @observable public guarantor: IRecord<SupportGuarantor | undefined> = createRecord(undefined, true);
    @observable public claimType: ClaimType = ClaimType.PROFESSIONAL;

    @observable public selectedClaimTypes: ClaimType[] = [ClaimType.PROFESSIONAL];

    // Marker that we already loaded inactive payments at least once
    @observable public hasLoadedInactivePayments: boolean = false;

    @action public selectClaimType = (type: ClaimType) => {
        this.selectedClaimTypes = [ClaimType.PROFESSIONAL, ClaimType.INSTITUTIONAL].filter(el => el === type); //types
        this.claimType = type;
    };

    public fetch = async (req?: IFetchResourceRequest): Promise<void> => {
        runInAction(() => {
            this.loading = true;
        });

        const reqOptions = this.handleCreateRequestOptions(req, SupportClaimSortType, ListSupportClaimsOrderEnum);
        (reqOptions as ListSupportClaimsRequest).filterClaimType =
            this.claimType === ClaimType.PROFESSIONAL
                ? ListSupportClaimsFilterClaimTypeEnum.PROFESSIONAL
                : ListSupportClaimsFilterClaimTypeEnum.INSTITUTIONAL;
        const raw = await CoreFetch<ClaimApi>(ClaimApi).listSupportClaimsRaw(reqOptions);

        await this.handleFetchResource(raw);
        // Wrap to run in action to not trigger each item in the list for observing
        runInAction(() => {
            this.resourceList = _.map(this.resourceList, claim => ClaimsStore.updateClaimStatus(claim));
            this.loading = false;
        });
    };

    public create = async (payload: SupportCreateClaimPayload): Promise<ObjectIdResponse> => {
        runInAction(() => {
            this.loading = true;
        });
        let claimRes: Promise<ObjectIdResponse>;

        if (payload.claimType === ClaimType.PROFESSIONAL && payload.supportCreateClaimProfessionalPayload) {
            claimRes = ClaimsStore.createProfessionalClaim(payload.supportCreateClaimProfessionalPayload);
        } else if (payload.claimType === ClaimType.INSTITUTIONAL && payload.supportCreateClaimInstitutionalPayload) {
            claimRes = ClaimsStore.createInstitutionalClaim(payload.supportCreateClaimInstitutionalPayload);
        } else {
            throw new Error('Unable to create claim with unknown type');
        }

        runInAction(() => {
            this.loading = false;
        });

        return claimRes;
    };

    public static async sendClaim(
        id: string,
        position?: PayerResponsibilityType,
        type?: TransmissionType,
    ): Promise<void> {
        await CoreFetch<ClaimApi>(ClaimApi).sendClaim({
            id,
            supportSendClaimPayload: {
                position,
                type,
            },
        });
        return;
    }

    public loadOneWithSuperFastClaimDetails = async (id: string): Promise<SupportClaimDetails> => {
        runInAction(() => {
            this.loading = true;
        });
        const details = await CoreFetch<ClaimApi>(ClaimApi).getSupportClaimDetails({
            id,
        });

        const claim = details.claim;

        if (claim) {
            runInAction(() => {
                if (details.transmissions) {
                    this.transmissions = details.transmissions;
                }

                if (details.phones) {
                    this.phones = details.phones;
                }

                if (details.documents) {
                    this.documents = details.documents.filter(d => d.documentType !== 'PHONE');
                }

                if (details.payments) {
                    const activePayments = details.payments.filter(p => p.active !== false);
                    const inactivePayments = details.payments.filter(p => p.active === false);

                    this.payments = createRecord(activePayments, true);
                    // Always reset inactive payments based on the latest response
                    this.inactivePayments = createRecord(inactivePayments, true);
                    this.hasLoadedInactivePayments = inactivePayments.length > 0;
                }

                if (details.letters) {
                    this.letters = details.letters;
                }

                this.workerFunctions = details.workerFunctions ?? [];

                if (details.codings) {
                    this.codings = details.codings;
                }

                if (details.comments) {
                    this.comments = details.comments;
                }

                if (details.mails) {
                    this.mails = details.mails;
                }

                if (details.receiveFaxes) {
                    this.faxReceive = details.receiveFaxes;
                }

                if (details.sendFaxes) {
                    this.faxSend = details.sendFaxes;
                }

                if (details.hl7s) {
                    this.hl7 = details.hl7s;
                }

                if (details.messages) {
                    this.messages = details.messages;
                }

                if (details.collectionPayments) {
                    this.collectionPayments = createRecord(details.collectionPayments, true);
                }

                if (details.statuses) {
                    this.statuses = details.statuses;
                }

                if (details.invoices) {
                    this.invoices = createRecord(details.invoices, true);
                }

                if (details.collectionInvoiceEvents) {
                    this.collectionInvoiceEvents = details.collectionInvoiceEvents;
                }

                if (details.eligibilities) {
                    this.eligibilities = createRecord(details.eligibilities, true);
                }

                if (details.sendEmails) {
                    this.emailSend = details.sendEmails;
                }

                //Tags
                if (details.historicalTagRecords) {
                    const res: TagEvent[] = [];

                    details.historicalTagRecords.forEach(record => {
                        if (record.created) {
                            res.push({
                                id: record.id,
                                eventType: 'create',
                                created: record.created,
                                accountId: record.createdBy,
                                name: record.tagName || '',
                            });
                        }

                        if (record.disabled) {
                            res.push({
                                id: record.id,
                                eventType: 'disable',
                                created: record.disabled,
                                accountId: record.disabledBy,
                                name: record.tagName || '',
                            });
                        }
                    });

                    this.tags = res;
                }

                let supportMedicalClaimResponse: SupportMedicalClaimResponse | undefined;

                if (details.professionals) {
                    this.professionals = createRecord(details.professionals, true);
                    if (this.professionals && this.professionals.loaded && this.professionals.data.length > 0) {
                        const supportProfessionalClaim: SupportProfessionalClaim | undefined = _.last(
                            this.professionals.data,
                        );

                        if (supportProfessionalClaim) {
                            this.professionalTemplate.loaded = false;
                            this.chargemasterItems.loaded = false;

                            supportMedicalClaimResponse = supportProfessionalClaim as SupportMedicalClaimResponse;

                            //I know this can be a simple map, but typescript is yelling at me unless I break it out
                            const supportProfessionalChargemasterItems: SupportChargemasterItem[] = [];

                            if (
                                supportProfessionalClaim.professionalTemplate &&
                                supportProfessionalClaim.professionalTemplate.f24ServiceLine
                            ) {
                                for (const supportProfessionalTemplateServiceLine of supportProfessionalClaim
                                    .professionalTemplate.f24ServiceLine) {
                                    if (supportProfessionalTemplateServiceLine.chargemasterItem) {
                                        supportProfessionalChargemasterItems.push(
                                            supportProfessionalTemplateServiceLine.chargemasterItem,
                                        );
                                    }
                                }
                            }

                            this.professionalTemplate = createRecord(
                                supportProfessionalClaim.professionalTemplate,
                                true,
                            );

                            this.chargemasterItems = createRecord(supportProfessionalChargemasterItems, true);
                        }
                    }
                }

                if (details.institutionals) {
                    this.institutionals = createRecord(details.institutionals, true);
                    if (this.institutionals && this.institutionals.loaded && this.institutionals.data.length > 0) {
                        const supportInstitutionalClaim: SupportInstitutionalClaim | undefined = _.last(
                            this.institutionals.data,
                        );
                        if (supportInstitutionalClaim) {
                            this.institutionalTemplate.loaded = false;
                            this.chargemasterItems.loaded = false;

                            supportMedicalClaimResponse = supportInstitutionalClaim as SupportMedicalClaimResponse;

                            //I know this can be a simple map, but typescript is yelling at me unless I break it out
                            const supportInstitutionalChargemasterItems: SupportChargemasterItem[] = [];

                            if (
                                supportInstitutionalClaim.institutionalTemplate &&
                                supportInstitutionalClaim.institutionalTemplate.f42ClaimLines
                            ) {
                                for (const supportInstitutionalTemplateServiceLine of supportInstitutionalClaim
                                    .institutionalTemplate.f42ClaimLines) {
                                    if (supportInstitutionalTemplateServiceLine.chargemasterItem) {
                                        supportInstitutionalChargemasterItems.push(
                                            supportInstitutionalTemplateServiceLine.chargemasterItem,
                                        );
                                    }
                                }
                            }

                            this.institutionalTemplate = createRecord(
                                supportInstitutionalClaim.institutionalTemplate,
                                true,
                            );

                            this.chargemasterItems = createRecord(supportInstitutionalChargemasterItems, true);
                        }
                    }
                }

                if (supportMedicalClaimResponse) {
                    if (supportMedicalClaimResponse.providers) {
                        this.providers = supportMedicalClaimResponse.providers;
                    }
                    if (supportMedicalClaimResponse.facility) {
                        this.facility = supportMedicalClaimResponse.facility;
                    }
                    if (supportMedicalClaimResponse.seller) {
                        this.seller = supportMedicalClaimResponse.seller;
                    }
                    if (supportMedicalClaimResponse.guarantor) {
                        this.guarantor = createRecord(supportMedicalClaimResponse.guarantor, true);
                    }
                }

                this.loading = false;
            });

            return {
                ...details,
                claim: ClaimsStore.updateClaimStatus(claim),
            };
        } else {
            throw new Error('Invalid claim id');
        }
    };

    public getOne = async (id: string): Promise<SupportClaim> => {
        runInAction(() => {
            this.loading = true;
        });
        const claim = await CoreFetch<ClaimApi>(ClaimApi).getSupportClaim({
            id,
        });
        runInAction(() => {
            this.loading = false;
        });

        return ClaimsStore.updateClaimStatus(claim);
    };

    public createProfessional = async (payload: SupportCreateProfessionalClaimPayload): Promise<ObjectIdResponse> => {
        runInAction(() => {
            this.loading = true;
        });
        const claimRes = await CoreFetch<ProfessionalApi>(ProfessionalApi).createSupportProfessionalClaim({
            supportCreateProfessionalClaimPayload: payload,
        });
        runInAction(() => {
            this.loading = false;
        });

        return claimRes;
    };

    public createInstitutional = async (payload: SupportCreateInstitutionalClaimPayload): Promise<ObjectIdResponse> => {
        runInAction(() => {
            this.loading = true;
        });
        const claimRes = await CoreFetch<InstitutionalApi>(InstitutionalApi).createSupportInstitutionalClaim({
            supportCreateInstitutionalClaimPayload: payload,
        });
        runInAction(() => {
            this.loading = false;
        });

        return claimRes;
    };

    public updateProfessional = async (
        id: string,
        payload: SupportUpdateProfessionalClaimPayload,
    ): Promise<ObjectIdResponse> => {
        throw new Error('API no longer support this action');
    };

    public updateInstitutional = async (
        id: string,
        payload: SupportUpdateInstitutionalClaimPayload,
    ): Promise<ObjectIdResponse> => {
        runInAction(() => {
            this.loading = true;
        });
        const claimRes = await CoreFetch<InstitutionalApi>(InstitutionalApi).updateSupportInstitutionalClaim({
            id,
            supportUpdateInstitutionalClaimPayload: payload,
        });
        runInAction(() => {
            this.loading = false;
        });

        return claimRes;
    };

    public disable = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).disableSupportClaim({
            id,
        });
    };

    public enable = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).enableSupportClaim({
            id,
        });
    };

    public refreshChargemaster = async (id: string, force: boolean = false): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).refreshClaimChargemaster({
            id,
            force,
        });
    };

    public postSupportClaimPreparePatientCollections = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).postSupportClaimPreparePatientCollections({
            id,
        });
    };

    public postSupportClaimResyncAllScriptsDocuments = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).postSupportClaimResyncAllScriptsDocuments({
            id,
        });
    };

    public storeSftpDocumentsToClaimTask = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).storeSftpDocumentsToClaimTask({
            id,
        });
    };

    public fullyAugmentClaim = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).gatherClaimAugmentData({
            id,
            supportAugmentPayload: {
                storeEncounterNotes: true,
                updateEntities: true,
            },
        });
    };

    public createStatus = async (payload: SupportCreateStatusPayload): Promise<void> => {
        await CoreFetch<StatusApi>(StatusApi).createSupportStatus({
            supportCreateStatusPayload: payload,
        });
    };

    public invoice = async (id: string): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).generateInvoices({
            id,
        });
    };

    public runWorker = async (id: string): Promise<ClaimWorkerResponse> => {
        return await CoreFetch<ClaimApi>(ClaimApi).runClaimThroughWorker({
            id,
        });
    };

    public settle = async (_id: string, _payload: SupportSettleClaimPayload): Promise<void> => {
        return;
    };

    public editStrategy = async (id: string, payload: SupportAdjustCollectionStrategyPayload): Promise<void> => {
        await CoreFetch<ClaimApi>(ClaimApi).adjustClaimCollectionStrategy({
            id,
            supportAdjustCollectionStrategyPayload: payload,
        });
    };

    public addTags = async (id: string, tags: SupportTag[]): Promise<void> => {
        await Promise.all(
            tags
                .filter(tag => !!tag.id)
                .map(tag => {
                    if (!tag.id) {
                        throw new Error('Tag id is missing');
                    }

                    return CoreFetch<TagApi>(TagApi).createSupportTagRecordDeprecated({
                        tid: tag.id,
                        supportCreateTagRecordPayload: {
                            claimId: id,
                        },
                    });
                }),
        );
    };

    public removeTags = async (id: string, tags: SupportTag[]): Promise<void> => {
        await Promise.all(
            tags
                .filter(tag => !!tag.id)
                .map(async tag => {
                    if (!tag.id) {
                        throw new Error('Tag id is missing');
                    }

                    const records = await CoreFetch<TagApi>(TagApi).listSupportTagRecordsDeprecated({
                        filterActive: 'EQ:1',
                        tid: tag.id,
                        filterClaimId: [`EQ:${id}`],
                    });

                    const record = records[0];
                    if (!record?.id) {
                        throw new Error(`Can't find record for this resource`);
                    }

                    return CoreFetch<TagApi>(TagApi).disableSupportTagRecordDeprecated({
                        tid: tag.id,
                        rid: record.id,
                    });
                }),
        );
    };

    @action
    public getProfessionals = async (claimId: string): Promise<SupportProfessionalClaim[]> => {
        const professionals = await CoreFetch<ProfessionalApi>(ProfessionalApi).listSupportProfessionalClaims({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:1`,
        });

        this.professionals = createRecord(professionals, true);

        return professionals;
    };

    @action
    public getInstitutionals = async (claimId: string): Promise<SupportInstitutionalClaim[]> => {
        const institutionals = await CoreFetch<InstitutionalApi>(InstitutionalApi).listSupportInstitutionalClaims({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:1`,
        });

        this.institutionals = createRecord(institutionals, true);

        return institutionals;
    };

    @action
    public getNynf3 = async (claimId: string): Promise<SupportInstitutionalClaim[]> => {
        this.nynf3 = await CoreFetch<Nynf3Api>(Nynf3Api).listSupportNynf3Claims({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:1`,
        });

        return this.nynf3;
    };

    @action
    public updateMedicalClaim = (newClaim: SupportProfessionalClaim & SupportInstitutionalClaim) => {
        if (newClaim.professionalTemplateId) {
            this.professionals = createRecord(
                this.professionals.data.map(professional =>
                    professional.id === newClaim.id ? newClaim : professional,
                ),
                true,
            );
        } else if (newClaim.institutionalTemplateId) {
            this.institutionals = createRecord(
                this.institutionals.data.map(institutional =>
                    institutional.id === newClaim.id ? newClaim : institutional,
                ),
                true,
            );
        }
    };

    @action
    public updateProfessionalTemplate = (newTemplate: SupportProfessionalClaimTemplate) => {
        this.professionalTemplate = createRecord(newTemplate, true);
    };

    @action
    public updateGuarantor = (newGuarantor: SupportGuarantor) => {
        this.guarantor = createRecord(newGuarantor, true);
    };

    public getEligibilities = async (claimId: string): Promise<SupportClaimEligibility[]> => {
        const eligibilities = await CoreFetch<EligibilityApi>(EligibilityApi).listSupportClaimEligibilities({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:1`,
        });

        if (_.isEmpty(eligibilities)) {
            this.eligibilities = createRecord(this.eligibilities.data, true);
            return this.eligibilities.data;
        }

        this.eligibilities = createRecord(eligibilities, true);

        return eligibilities;
    };

    public getEstimates = async (claimId: string): Promise<SupportEstimate[]> => {
        this.estimates = await CoreFetch<EstimateApi>(EstimateApi).listSupportEstimates({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.estimates;
    };

    public getTransmissions = async (claimId: string): Promise<SupportClaimTransmission[]> => {
        this.transmissions = await CoreFetch<TransmissionApi>(TransmissionApi).listSupportClaimTransmissions({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.transmissions;
    };

    public getDocuments = async (claimId: string): Promise<SupportDocument[]> => {
        this.documents = await CoreFetch<DocumentApi>(DocumentApi).listSupportDocuments({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:1`,
        });

        if (this.documents) {
            this.documents = this.documents.filter(d => d.documentType !== 'PHONE');
        }

        return this.documents;
    };

    @action
    public disableDocument = (documentId: string) => {
        this.documents = this.documents.filter(el => el.id !== documentId);
    };

    public getPayments = async (claimId: string): Promise<SupportClaimPayment[]> => {
        const payments = await CoreFetch<PaymentApi>(PaymentApi).listSupportClaimPayments({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:1`,
        });

        this.payments = createRecord(payments, true);

        return payments;
    };

    public loadInactivePayments = async (claimId: string): Promise<SupportClaimPayment[]> => {
        const payments = await CoreFetch<PaymentApi>(PaymentApi).listSupportClaimPayments({
            filterClaimId: [`EQ:${claimId}`],
            filterActive: `EQ:0`,
        });

        runInAction(() => {
            this.inactivePayments = createRecord(payments, true);
            this.hasLoadedInactivePayments = true;
        });

        return payments;
    };

    public getCollections = async (claimId: string): Promise<SupportClaimPayment[]> => {
        const collectionPayments = await CoreFetch<CollectionApi>(CollectionApi).listSupportCollectionPayments({
            filterClaimId: [`EQ:${claimId}`],
        });

        this.collectionPayments = createRecord(collectionPayments, true);

        return collectionPayments;
    };

    @action
    public getChargemasterItemsByProfessional = async (
        professional: SupportProfessionalClaim,
    ): Promise<SupportClaimPayment[]> => {
        this.chargemasterItems.loaded = false;

        if (!professional.professionalTemplateId || !professional.chargemasterId) {
            this.chargemasterItems = createRecord(this.chargemasterItems.data, true);

            return this.chargemasterItems.data;
        }

        this.professionalTemplate.loaded = false;
        const template = await ProfessionalTemplatesStore.getOne(professional.professionalTemplateId);
        this.professionalTemplate = createRecord(template, true);

        const chargemasterItemIds = _.compact(_.map(template?.f24ServiceLine, 'chargemasterItemId'));

        const chargemasterItems = await this.getChargemasterItems(chargemasterItemIds, professional.chargemasterId);

        this.chargemasterItems = createRecord(chargemasterItems, true);

        return chargemasterItems;
    };

    public getChargemasterItemsByInstitutional = async (
        institutional: SupportInstitutionalClaim,
    ): Promise<SupportClaimPayment[]> => {
        this.chargemasterItems.loaded = false;

        if (!institutional.institutionalTemplateId || !institutional.chargemasterId) {
            this.chargemasterItems = createRecord(this.chargemasterItems.data, true);
            return this.chargemasterItems.data;
        }

        this.institutionalTemplate.loaded = false;
        const template = await InstitutionalTemplatesStore.getOne(institutional.institutionalTemplateId);
        this.institutionalTemplate = createRecord(template, true);

        const chargemasterItemIds = _.compact(_.map(template?.f42ClaimLines, 'chargemasterItemId'));

        const chargemasterItems = await this.getChargemasterItems(chargemasterItemIds, institutional.chargemasterId);

        this.chargemasterItems = createRecord(chargemasterItems, true);

        return chargemasterItems;
    };

    public getChargemasterItemsByNynf3 = async (nynf3: SupportNynf3Claim): Promise<SupportClaimPayment[]> => {
        if (!nynf3.nynf3TemplateId || !nynf3.chargemasterId) {
            return this.chargemasterItems.data;
        }

        throw new Error('NYNF3 claims are not supported');
    };

    public maybeGetGuarantor = async (id?: string): Promise<SupportGuarantor | undefined> => {
        this.guarantor.loaded = false;

        if (!id) {
            this.guarantor = createRecord(this.guarantor.data, true);
            return;
        }

        const guarantor = await CoreFetch<GuarantorApi>(GuarantorApi).getSupportGuarantor({
            id,
        });

        this.guarantor = createRecord(guarantor, true);

        return guarantor;
    };

    public getInvoices = async (claimId: string): Promise<Invoice[]> => {
        this.invoices.loaded = false;

        const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listSupportInvoices({
            filterClaimId: [`EQ:${claimId}`],
            filterInvoiceType: [`EQ:${InvoiceType.PATIENT}`],
        });

        this.invoices = createRecord(invoices, true);

        return invoices;
    };

    public getLetters = async (claimId: string): Promise<Invoice[]> => {
        const letters = await CoreFetch<LetterApi>(LetterApi).listSupportLetters({
            filterClaimId: [`EQ:${claimId}`],
        });

        this.letters = letters.filter(letter => !letter.cancelled);

        return this.letters;
    };

    public getCodings = async (claimId: string): Promise<SupportCoding[]> => {
        this.codings = await CoreFetch<CodingApi>(CodingApi).listSupportCodings({
            filterClaimId: [`EQ:${claimId}`],
        });
        return this.codings;
    };

    public getCollectionInvoiceEvents = async (claimId: string): Promise<SupportCollectionInvoiceEvent[]> => {
        this.collectionInvoiceEvents = await CoreFetch<CollectionApi>(CollectionApi).listSupportCollectionInvoiceEvents(
            {
                filterClaimId: [`EQ:${claimId}`],
            },
        );
        return this.collectionInvoiceEvents;
    };

    public getMails = async (claimId: string): Promise<Invoice[]> => {
        this.mails = await CoreFetch<MailApi>(MailApi).listSupportMail({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.mails;
    };

    public getReceiveFaxes = async (claimId: string): Promise<Invoice[]> => {
        this.faxReceive = await CoreFetch<FaxApi>(FaxApi).listSupportReceiveFaxes({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.faxReceive;
    };

    public getSendEmails = async (claimId: string): Promise<SupportSendEmail[]> => {
        this.emailSend = await CoreFetch<EmailApi>(EmailApi).listSupportSendEmails({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.emailSend;
    };

    public getPhones = async (claimId: string): Promise<SupportPhone[]> => {
        this.phones = await CoreFetch<PhoneApi>(PhoneApi).listSupportPhone({
            filterClaimId: [`EQ:${claimId}`],
        });
        return this.phones;
    };

    public getSendFaxes = async (claimId: string): Promise<Invoice[]> => {
        const maxIterations = 10;

        const tempSendFaxes: SupportSendFax[] = [];
        let offset = 0;
        let previousLength = 0;

        for (let i = 0; i < maxIterations; i++) {
            tempSendFaxes.push(
                ...(await CoreFetch<FaxApi>(FaxApi).listSupportSendFaxes({
                    filterClaimId: [`EQ:${claimId}`],
                    limit: 10,
                    offset,
                })),
            );

            if (tempSendFaxes.length > previousLength && tempSendFaxes.length > 0 && tempSendFaxes.length % 10 == 0) {
                offset += 10;
                previousLength = tempSendFaxes.length;
            } else {
                break;
            }
        }

        this.faxSend = tempSendFaxes;

        return this.faxSend;
    };

    public getTransactions = async (_claimId: string): Promise<Invoice[]> => {
        this.transactions = [];
        return this.transactions;
    };

    public getHl7ForReprocess = async (claimId: string): Promise<SupportHL7Message[]> => {
        //Sort descending to get the most recent HL7s
        this.hl7 = await CoreFetch<HL7Api>(HL7Api).listSupportHL7Messages({
            filterClaimId: [`EQ:${claimId}`],
            order: [ListSupportHL7MessagesOrderEnum.DESCENDING],
            sort: [SupportHL7SortType.CREATED],
        });

        //Get rid of the JSON payloads if the first one is not JSON
        if (this.hl7 && this.hl7.length > 0) {
            const firstHL7Record = this.hl7[0];
            if (firstHL7Record.type) {
                if (firstHL7Record.type.toString().indexOf('JSON') === -1) {
                    this.hl7 = this.hl7.filter(v => v.type && v.type.toString().indexOf('JSON') === -1);
                }
            }
        }

        return this.hl7;
    };

    public getHl7 = async (claimId: string): Promise<SupportHL7Message[]> => {
        //Only get the earliest HL7 payload
        this.hl7 = await CoreFetch<HL7Api>(HL7Api).listSupportHL7Messages({
            filterClaimId: [`EQ:${claimId}`],
            order: [ListSupportHL7MessagesOrderEnum.ASCENDING],
            sort: [SupportHL7SortType.CREATED],
        });

        //Get rid of the JSON payloads if the first one is not JSON
        if (this.hl7 && this.hl7.length > 0) {
            const firstHL7Record = this.hl7[0];
            if (firstHL7Record.type) {
                if (firstHL7Record.type.toString().indexOf('JSON') === -1) {
                    this.hl7 = this.hl7.filter(v => v.type && v.type.toString().indexOf('JSON') === -1);
                }
            }
        }

        //If we have more than 1, just take the earliest
        // if(this.hl7 && this.hl7.length > 1) {
        //     this.hl7 = [this.hl7[this.hl7.length - 1]]
        // }

        return this.hl7;
    };

    public getMessages = async (claimId: string): Promise<SupportMessage[]> => {
        this.messages = await CoreFetch<MessageApi>(MessageApi).listSupportMessages({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.messages;
    };

    public getStatuses = async (claimId: string): Promise<SupportStatus[]> => {
        this.statuses = await CoreFetch<StatusApi>(StatusApi).listSupportStatuses({
            filterClaimId: [`EQ:${claimId}`],
        });

        return this.statuses;
    };

    public getProviders = async (providerIds: string[]): Promise<SupportProvider[]> => {
        if (_.isEmpty(providerIds)) {
            return [];
        }

        const filters: ICustomFilter[][] = [
            providerIds.map(id => ({
                filterId: 'filterId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters);
        this.providers = await CoreFetch<ProviderApi>(ProviderApi).listSupportProviders({
            ...reqFilters,
        });

        return this.providers;
    };

    public getFacility = async (facilityId: string | undefined): Promise<SupportFacility | undefined> => {
        if (!facilityId) {
            return;
        }

        this.facility = await CoreFetch<FacilityApi>(FacilityApi).getSupportFacility({
            id: facilityId,
        });

        return this.facility;
    };

    public getSeller = async (sellerId: string | undefined): Promise<SupportSeller | undefined> => {
        if (!sellerId) {
            return;
        }

        this.seller = await CoreFetch<SellerApi>(SellerApi).getSupportSeller({
            id: sellerId,
        });

        return this.seller;
    };

    public getTags = async (claimId: string): Promise<TagEvent[]> => {
        const tags = await CoreFetch<TagApi>(TagApi).listSupportTags({
            filterClaimId: [`EQ:${claimId}`],
        });

        if (tags.length === 0) {
            return [];
        }

        const res: TagEvent[] = [];

        const filters: ICustomFilter[][] = [
            tags.map(tag => ({
                filterId: 'tid',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: tag.id!,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters, true) as any;

        const tagRecords = await CoreFetch<TagApi>(TagApi).listSupportTagRecords({
            filterClaimId: [`EQ:${claimId}`],
            ...reqFilters,
        });

        tagRecords.forEach(record => {
            if (record.created) {
                res.push({
                    id: record.id,
                    eventType: 'create',
                    created: record.created,
                    accountId: record.createdBy,
                    name: record.tagName || '',
                });
            }

            if (record.disabled) {
                res.push({
                    id: record.id,
                    eventType: 'disable',
                    created: record.disabled,
                    accountId: record.disabledBy,
                    name: record.tagName || '',
                });
            }
        });

        this.tags = res;

        return this.tags;
    };

    private getAccounts = (ids: string[]): Promise<SupportAccount[]> => {
        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters, true) as any;

        return CoreFetch<AdminApi>(AdminApi).getSupportAccountList({
            ...reqFilters,
        });
    };

    private getChargemasterItems = (ids: string[], chargemasterId: string) => {
        if (_.isEmpty(ids)) {
            return [];
        }

        const filters: ICustomFilter[][] = [
            ids.map(id => ({
                filterId: 'filterId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: id,
            })),
        ];

        const reqFilters = createGroupRequestFilters(filters, true);

        return CoreFetch<ChargemasterApi>(ChargemasterApi).listSupportChargemasterItems({
            id: chargemasterId,
            ...reqFilters,
        });
    };

    @action
    public resetStore = () => {
        // Templates, chargemaster items and invoices go back to the SAME state a fresh store starts
        // in — loaded, empty. Only a claim with a generated professional/institutional claim, or an
        // invoice fetch, flips them to "loading" and back. Resetting them to not-loaded left every
        // claim without one on a permanent Record of Services skeleton (and no Claim Progress
        // totals) after details → Edit → Cancel/Save, while F5 — a fresh store — rendered fine.
        this.professionalTemplate = createRecord(undefined, true);
        this.institutionalTemplate = createRecord(undefined, true);
        this.professionals = createRecord([]);
        this.institutionals = createRecord([]);
        this.nynf3 = [];
        this.eligibilities = createRecord([]);
        this.estimates = [];
        this.transmissions = [];
        this.documents = [];
        this.payments = createRecord([]);
        this.inactivePayments = createRecord([]);
        this.chargemasterItems = createRecord([], true);
        this.invoices = createRecord([], true);
        this.letters = [];
        this.mails = [];
        this.faxSend = [];
        this.faxReceive = [];
        this.transactions = [];
        this.hl7 = [];
        this.messages = [];
        this.tags = [];
        this.statuses = [];
        this.providers = [];
        this.facility = undefined;
        this.seller = undefined;
        this.hasLoadedInactivePayments = false;
    };
}

export default new ClaimsStore();
